import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface Filters {
  searchTerm: string;
  typologie: string;
  modalite: string;
  devolution: string;
  gamme: string;
  annee: string;
  localisation: string;
  projet: string;
  fournisseur: string;
  categorie: string;
  unite: string;
  prixMin: string;
  prixMax: string;
  surfaceMin: string;
  surfaceMax: string;
}

interface FilterContextType {
  filters: Filters;
  setFilters: (filters: Filters) => void;
  updateFilter: (field: keyof Filters, value: string) => void;
  resetFilters: () => void;
  hasActiveFilters: boolean;
}

const FilterContext = createContext<FilterContextType | undefined>(undefined);

export const useFilters = () => {
  const context = useContext(FilterContext);
  if (!context) {
    throw new Error('useFilters must be used within a FilterProvider');
  }
  return context;
};

interface FilterProviderProps {
  children: ReactNode;
}

export const FilterProvider: React.FC<FilterProviderProps> = ({ children }) => {
  const [filters, setFilters] = useState<Filters>({
    searchTerm: '',
    typologie: '',
    modalite: '',
    devolution: '',
    gamme: '',
    annee: '',
    localisation: '',
    projet: '',
    fournisseur: '',
    categorie: '',
    unite: '',
    prixMin: '',
    prixMax: '',
    surfaceMin: '',
    surfaceMax: ''
  });

  const updateFilter = (field: keyof Filters, value: string) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const resetFilters = () => {
    setFilters({
      searchTerm: '',
      typologie: '',
      modalite: '',
      devolution: '',
      gamme: '',
      annee: '',
      localisation: '',
      projet: '',
      fournisseur: '',
      categorie: '',
      unite: '',
      prixMin: '',
      prixMax: '',
      surfaceMin: '',
      surfaceMax: ''
    });
  };

  const hasActiveFilters = Object.values(filters).some(value => value !== '');

  // Log des changements de filtres
  useEffect(() => {
    if (hasActiveFilters) {
      console.log('🔄 Filtres mis à jour:', filters);
    }
  }, [filters, hasActiveFilters]);

  return (
    <FilterContext.Provider value={{
      filters,
      setFilters,
      updateFilter,
      resetFilters,
      hasActiveFilters
    }}>
      {children}
    </FilterContext.Provider>
  );
}; 